import { Outlet, useNavigate } from 'react-router-dom';
import { TypedNavigate, isPageAllowed, useTypedLocation } from 'ck-utils';

import { FullScreenLoadingSpinner } from 'ck-components';
import { Navbar } from 'ck-features';
import { useAuth } from 'ck-queries';
import { useEffect, useState } from 'react';

/**
 * This is the Root component that wraps all authenticated routes to provide styling and a Navbar.
 * The <Outlet /> is where all child routes to '/' will be rendered.
 * To see all routes go to src/index.tsx
 * @returns Root page wrapper component
 */
export default function Root() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useTypedLocation();
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);

  const isTransferTicketPage =
    location.pathname.startsWith('/transfer-ticket/');

  // Redirect if we don't have a user
  useEffect(() => {
    if (!user && !isTransferTicketPage) {
      navigate('/login');
    }
  }, [user, navigate]);

  // Close mobile nav on any navigation (pathname, search, or hash change)
  useEffect(() => {
    setIsMobileNavOpen(false);
  }, [location.key]);

  // Allow access to transfer-ticket/{id} without authentication
  if (isTransferTicketPage) {
    return <Outlet />;
  }

  if (!user) {
    return <FullScreenLoadingSpinner />;
  }

  // Only render this if the user has the correct function
  return (
    <div className='flex min-h-screen w-full flex-row'>
      {/* Hamburger button — mobile only, hidden when nav is open */}
      <button
        className={`fixed left-4 top-4 z-50 flex h-10 w-10 flex-col items-center justify-center gap-1.5 rounded-md bg-white shadow-md md:hidden ${
          isMobileNavOpen ? 'hidden' : ''
        }`}
        onClick={() => setIsMobileNavOpen(true)}
        aria-label='Öppna navigation'
        aria-expanded={isMobileNavOpen}
        aria-controls='mobile-nav'
      >
        <span className='block h-0.5 w-5 bg-gray-800' />
        <span className='block h-0.5 w-5 bg-gray-800' />
        <span className='block h-0.5 w-5 bg-gray-800' />
      </button>

      {/* Backdrop — always rendered on mobile, fades in/out in sync with the nav slide */}
      <div
        className={`fixed inset-0 z-40 bg-black/40 transition-opacity duration-300 md:hidden ${
          isMobileNavOpen
            ? 'pointer-events-auto opacity-100'
            : 'pointer-events-none opacity-0'
        }`}
        onClick={() => setIsMobileNavOpen(false)}
      />

      {/* Sidebar spacer — only takes up space on desktop */}
      <div className='hidden md:relative md:block md:h-screen md:min-w-[305px]' />

      <Navbar
        isOpen={isMobileNavOpen}
        onClose={() => setIsMobileNavOpen(false)}
      />

      <div className='w-full bg-[#FCFCFC] px-4 py-16 md:px-10 md:py-20'>
        <div className='min-w-0'>
          {isPageAllowed(user, location.pathname) ? (
            <Outlet />
          ) : (
            <TypedNavigate to='/' />
          )}
        </div>
      </div>
    </div>
  );
}
